import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { B, PD } from "../theme";
import { GLOSSARY } from "../content/astro/glossary";

/* Tap-to-explain term. Renders the label with a dotted underline; tapping
   opens a small definition card anchored under it. One open at a time:
   opening another closes this one (via a window event). */

const OPEN_EVENT = "theweb:term-open";

export function Term({ k, children, more, accent = "#9AA6D6" }) {
  const entry = GLOSSARY[k];
  const [open, setOpen] = useState(false);
  const idRef = useRef(Math.random().toString(36).slice(2));
  const wrapRef = useRef(null);
  const noteRef = useRef(null);
  const [shift, setShift] = useState(0);
  const btnRef = useRef(null);
  const [padX, setPadX] = useState(2);

  // A 44px tap area that doesn't move the text: padding grows the button,
  // an equal negative margin gives the space back to the line. Short words
  // get wider side padding so they reach 44px across too.
  useLayoutEffect(() => {
    const el = btnRef.current;
    if (!el) return;
    const fit = () => {
      const label = el.getBoundingClientRect().width - 2 * parseFloat(getComputedStyle(el).paddingLeft);
      setPadX(Math.max(2, Math.ceil((44 - label) / 2)));
    };
    fit();
    if (document.fonts?.ready) document.fonts.ready.then(fit);
  }, [children]);

  // Keep the card inside the viewport (16px gutter) on narrow screens.
  useLayoutEffect(() => {
    if (!open || !noteRef.current) { setShift(0); return; }
    const r = noteRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const base = r.left - shift;
    let next = 0;
    if (base < 16) next = 16 - base;
    else if (base + r.width > vw - 16) next = vw - 16 - (base + r.width);
    if (Math.abs(next - shift) > 0.5) setShift(next);
  }, [open, shift]);

  useEffect(() => {
    const onOther = (e) => { if (e.detail !== idRef.current) setOpen(false); };
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    window.addEventListener(OPEN_EVENT, onOther);
    document.addEventListener("pointerdown", onDoc);
    return () => {
      window.removeEventListener(OPEN_EVENT, onOther);
      document.removeEventListener("pointerdown", onDoc);
    };
  }, []);

  if (!entry) return <>{children}</>;

  const toggle = (e) => {
    e.stopPropagation();
    const next = !open;
    setOpen(next);
    if (next) window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: idRef.current }));
  };

  return (
    <span ref={wrapRef} style={{ position: "relative", display: "inline" }}>
      <button
        type="button"
        ref={btnRef}
        data-term={k}
        onClick={toggle}
        aria-expanded={open}
        style={{
          font: "inherit", color: "inherit", background: "none", border: "none", padding: `15px ${padX}px`, margin: `-15px -${padX}px`, display: "inline-block",
          cursor: "help", textDecoration: "underline dotted", textDecorationColor: `${accent}AA`,
          textUnderlineOffset: "3px", letterSpacing: "inherit", textTransform: "inherit", textAlign: "inherit", lineHeight: "inherit",
        }}
      >
        {children || entry.term}
      </button>
      {open && (
        <span
          ref={noteRef}
          role="note"
          style={{
            position: "absolute", left: "50%", top: "calc(100% + 8px)", transform: `translateX(calc(-50% + ${shift}px))`,
            width: "min(280px, 80vw)", zIndex: 50, display: "block",
            background: "rgba(22,24,44,0.97)", border: `1px solid ${accent}55`, borderRadius: 12,
            padding: "12px 14px", boxShadow: "0 10px 30px rgba(0,0,0,0.45)",
            ...B, fontSize: 13, lineHeight: 1.55, color: PD.text, textAlign: "left",
            textTransform: "none", letterSpacing: 0, fontWeight: 400, fontStyle: "normal",
            whiteSpace: "normal",
          }}
        >
          <span style={{ display: "block", fontWeight: 600, color: accent, marginBottom: 4, fontSize: 13, letterSpacing: "0.04em" }}>
            {entry.term}
          </span>
          {entry.def}
          {more && <span style={{ display: "block", marginTop: 6, color: PD.sub }}>{more}</span>}
        </span>
      )}
    </span>
  );
}

/* Wraps the first match of each [pattern, key] pair in a <Term>. Pass the
   same `used` Set across paragraphs so a term is explained once per screen. */
export function linkTerms(text, pairs, accent, used = new Set()) {
  let parts = [text];
  pairs.forEach(([pattern, k]) => {
    if (used.has(k)) return;
    parts = parts.flatMap((part) => {
      if (typeof part !== "string" || used.has(k)) return [part];
      const m = part.match(pattern);
      if (!m) return [part];
      used.add(k);
      return [
        part.slice(0, m.index),
        <Term key={k} k={k} accent={accent}>{m[0]}</Term>,
        part.slice(m.index + m[0].length),
      ];
    });
  });
  return parts.filter((part) => part !== "");
}
